import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LAST_SITE_SLUG_KEY, useCurrentSiteOptional } from '@/contexts/SiteContext';
import { useToast } from '@/hooks/useToast';
import { useHelpDrawer } from '@/components/help/HelpDrawerContext';
import { HELP_ARTICLES } from '@/content/help/_index';
import { UISimulation } from '@/components/help/UISimulation';
import { ProcessDiagram } from '@/components/help/ProcessDiagram';
import { Video } from '@/components/help/Video';
import { WizardSimulation } from '@/components/help/simulations/WizardSimulation';
import { DashboardLayoutSimulation } from '@/components/help/simulations/DashboardLayoutSimulation';
import { EndToEndSimulation } from '@/components/help/simulations/EndToEndSimulation';
import { PipelineLoopSimulation } from '@/components/help/simulations/PipelineLoopSimulation';
import {
  EditorOverviewSimulation,
  ShorthandInsertSimulation,
  InterlinkPopoverSimulation,
  FactualFlagPopoverSimulation,
  AuthorityCitationSimulation,
  AnnotationSimulation,
  IllustrationSimulation,
  VisibilityToggleSimulation,
  FocusModeSimulation,
} from '@/components/help/simulations/EditorSimulations';
import {
  BrainstormSimulation,
  ApproveBriefSimulation,
  PipelineMonitorSimulation,
  CancelForceSimulation,
} from '@/components/help/simulations/PipelineSimulations';
import {
  PreviewModalSimulation,
  UnpublishSimulation,
  BulkOperationsSimulation,
  ArchiveDeleteSimulation,
} from '@/components/help/simulations/PublishSimulations';
import { RevisionSimulation } from '@/components/help/simulations/RevisionSimulation';
import {
  SiteManagementSimulation,
  MultiSiteNavSimulation,
  SiteHubSimulation,
  SearchFilterSortSimulation,
} from '@/components/help/simulations/SiteSimulations';
import { ThemeCustomizationSimulation } from '@/components/help/simulations/ThemeSimulations';
import {
  AIPromptGenerationSimulation,
  PromptTestingSimulation,
  PromptSyncingSimulation,
} from '@/components/help/simulations/PromptSimulations';
import { ComponentPackSimulation } from '@/components/help/simulations/ComponentPackSimulations';
import { ImageUploadSimulation } from '@/components/help/simulations/ImageSimulations';
import { AuthoritySourcesSimulation } from '@/components/help/simulations/AuthoritySimulations';
import { AnalyticsSimulation } from '@/components/help/simulations/AnalyticsSimulations';
import { AlertsSimulation } from '@/components/help/simulations/AlertsSimulations';
import { SettingsApiKeysSimulation } from '@/components/help/simulations/SettingsSimulations';
import { BrainstormSuggestionsSimulation } from '@/components/help/simulations/BrainstormSuggestionsSimulation';
import { ExportSimulation } from '@/components/help/simulations/ExportSimulation';
import {
  PipelineStatesDiagram,
  ArticleLifecycleDiagram,
  BriefLifecycleDiagram,
  AnnotationLifecycleDiagram,
} from '@/components/help/simulations/StateDiagrams';
import {
  AEODiagram,
  AIPipelineMentalModelDiagram,
  PromptArchitectureDiagram,
  PublishingInfrastructureDiagram,
  ThemingSystemDiagram,
  ComponentSystemDiagram,
} from '@/components/help/simulations/ConceptDiagrams';

interface CalloutProps {
  variant: 'tip' | 'warning' | 'info';
  children: ReactNode;
}

const CALLOUT_STYLES: Record<CalloutProps['variant'], string> = {
  tip: 'bg-success/10 border-success/40 text-success',
  warning: 'bg-warning/10 border-warning/40 text-warning',
  info: 'bg-accent/10 border-accent/40 text-accent',
};

const CALLOUT_LABEL: Record<CalloutProps['variant'], string> = {
  tip: 'Tip',
  warning: 'Warning',
  info: 'Info',
};

export function Callout({ variant, children }: CalloutProps): JSX.Element {
  const palette = CALLOUT_STYLES[variant];
  return (
    <aside
      role="note"
      className={'my-4 rounded-md border px-4 py-3 text-sm ' + palette}
    >
      <div className="text-[10px] font-mono uppercase tracking-wide mb-1 opacity-80">
        {CALLOUT_LABEL[variant]}
      </div>
      <div className="text-dash-primary">{children}</div>
    </aside>
  );
}

interface KeysProps {
  combo: string;
}

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.platform?.toLowerCase().includes('mac') ?? false;
}

export function Keys({ combo }: KeysProps): JSX.Element {
  const parts = combo.split('+').map((p) => p.trim());
  const mac = isMac();
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {parts.map((key, i) => {
        let display = key;
        if (key === 'Cmd' || key === 'CmdOrCtrl' || key === 'Meta') {
          display = mac ? '⌘' : 'Ctrl';
        } else if (key === 'Ctrl' && mac) {
          display = '⌘';
        }
        return (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 ? <span className="text-dash-muted text-xs">+</span> : null}
            <kbd className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-dash-border bg-dash-surface text-dash-primary">
              {display}
            </kbd>
          </span>
        );
      })}
    </span>
  );
}

interface RouteRefProps {
  to: string;
  children: ReactNode;
}

export function RouteRef({ to, children }: RouteRefProps): JSX.Element {
  const navigate = useNavigate();
  const siteCtx = useCurrentSiteOptional();
  const toast = useToast();
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    let target = to;
    if (target.includes(':slug')) {
      let slug = siteCtx?.currentSite?.slug ?? null;
      if (!slug && typeof window !== 'undefined') {
        try {
          slug = window.localStorage.getItem(LAST_SITE_SLUG_KEY);
        } catch {
          slug = null;
        }
      }
      if (!slug) {
        toast.push('Select a site first', 'info');
        navigate('/sites');
        return;
      }
      target = target.replace(':slug', slug);
    }
    navigate(target);
  };
  return (
    <a
      href={to}
      onClick={onClick}
      className="text-accent underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  );
}

interface ArticleRefProps {
  slug: string;
  children: ReactNode;
}

const warnedSlugs = new Set<string>();

export function ArticleRef({ slug, children }: ArticleRefProps): JSX.Element {
  const { isOpen, closeDrawer } = useHelpDrawer();
  const exists = HELP_ARTICLES.some((a) => a.slug === slug);
  useEffect(() => {
    if (!exists && !warnedSlugs.has(slug)) {
      warnedSlugs.add(slug);
      // One-time warning per unknown slug.

      console.warn(`ArticleRef: unknown help article slug "${slug}"`);
    }
  }, [exists, slug]);
  if (!exists) {
    return <span>{children}</span>;
  }
  return (
    <Link
      to={`/help/${slug}`}
      onClick={() => {
        if (isOpen) closeDrawer();
      }}
      className="text-accent underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </Link>
  );
}

export function AdminBadge(): JSX.Element {
  return (
    <span
      className="inline-flex items-center bg-warning/15 text-warning border border-warning/40 rounded px-2 py-0.5 text-[10px] uppercase tracking-wide font-mono"
      aria-label="Admin-only feature"
    >
      Admin
    </span>
  );
}

export {
  UISimulation,
  ProcessDiagram,
  Video,
  WizardSimulation,
  DashboardLayoutSimulation,
  EndToEndSimulation,
  PipelineLoopSimulation,
};

// eslint-disable-next-line react-refresh/only-export-components
export const helpMdxComponents = {
  Callout,
  Keys,
  RouteRef,
  ArticleRef,
  AdminBadge,
  UISimulation,
  ProcessDiagram,
  Video,
  WizardSimulation,
  DashboardLayoutSimulation,
  EndToEndSimulation,
  PipelineLoopSimulation,
  // K2 editor surface
  EditorOverviewSimulation,
  ImageUploadSimulation,
  ShorthandInsertSimulation,
  InterlinkPopoverSimulation,
  FactualFlagPopoverSimulation,
  AuthorityCitationSimulation,
  AnnotationSimulation,
  IllustrationSimulation,
  VisibilityToggleSimulation,
  FocusModeSimulation,
  // K2 pipeline
  BrainstormSimulation,
  ApproveBriefSimulation,
  PipelineMonitorSimulation,
  CancelForceSimulation,
  // K2 publish
  PreviewModalSimulation,
  UnpublishSimulation,
  BulkOperationsSimulation,
  ArchiveDeleteSimulation,
  // K2 revisions
  RevisionSimulation,
  // K3 sites + nav
  SiteManagementSimulation,
  MultiSiteNavSimulation,
  SiteHubSimulation,
  SearchFilterSortSimulation,
  // K3 theme
  ThemeCustomizationSimulation,
  // K3 prompts
  AIPromptGenerationSimulation,
  PromptTestingSimulation,
  PromptSyncingSimulation,
  // K3 packs + authority
  ComponentPackSimulation,
  AuthoritySourcesSimulation,
  // K3 analytics + alerts + settings
  AnalyticsSimulation,
  AlertsSimulation,
  SettingsApiKeysSimulation,
  // K3 brainstorm + export
  BrainstormSuggestionsSimulation,
  ExportSimulation,
  // K4 reference state diagrams
  PipelineStatesDiagram,
  ArticleLifecycleDiagram,
  BriefLifecycleDiagram,
  AnnotationLifecycleDiagram,
  // K5 concept diagrams
  AEODiagram,
  AIPipelineMentalModelDiagram,
  PromptArchitectureDiagram,
  PublishingInfrastructureDiagram,
  ThemingSystemDiagram,
  ComponentSystemDiagram,
};
